import { TarifsGrid } from "../components/TarifsGrid";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { homeFor } from "../lib/routes";
import { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { CountryGrid } from "../components/CountryPicker";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({
    nom: "", email: "", password: "", telephone: "",
    role: inviteToken ? "therapeute" : (params.get("role") || "patient"), langue: "fr", ville: "", centre_nom: "", code_parrain: params.get("parrain") || "",
    pays: Intl.DateTimeFormat().resolvedOptions().timeZone?.startsWith("Europe/") ? "FR" : "CD",
    region: Intl.DateTimeFormat().resolvedOptions().timeZone?.startsWith("Europe/") ? "europe" : "afrique",
  });
  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/transmission/invite/${inviteToken}`).then(({ data }) => { setInvite(data); setForm((f) => ({ ...f, nom: data.nom || f.nom, role: "therapeute" })); }).catch(() => setInvite({ invalid: true }));
  }, [inviteToken]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await register({ ...form, email: form.email.trim().toLowerCase(), invite_token: inviteToken && invite && !invite.invalid ? inviteToken : null });
      toast.success("Compte créé !");
      navigate(homeFor(u), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bc-shell" data-testid="register-page">
      <div className="flex-1 flex flex-col px-7 pt-10 pb-8 overflow-y-auto bc-scroll">
        <div className="flex items-center gap-3 mb-6">
          <Logo size={48} />
          <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426]">Créer mon compte</h1>
        </div>
        {invite && !invite.invalid && (
          <div data-testid="register-invite-banner" className="bb-hero bb-grain relative rounded-3xl p-4 mb-4 text-white">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-bold">Parrainage Bombacare</div>
            <div className="font-[Fraunces] text-lg font-bold leading-tight mt-1">{invite.parrain_nom} vous invite à devenir une nouvelle branche{invite.centre_nom ? ` de ${invite.centre_nom}` : ""}.</div>
            {invite.message && <div className="text-[12px] text-white/80 mt-1 italic">« {invite.message} »</div>}
          </div>
        )}
        {invite?.invalid && <div data-testid="register-invite-invalid" className="rounded-2xl bg-[#D9534F]/10 text-[#D9534F] text-[13px] px-4 py-3 mb-4">Lien de parrainage expiré ou déjà utilisé — vous pouvez créer un compte sans parrain.</div>}
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <Label htmlFor="nom" className="text-[#0A1426] font-semibold">Nom complet</Label>
            <Input id="nom" data-testid="register-name-input" required value={form.nom} onChange={onChange("nom")} className="h-14 bg-white" />
          </div>
          <div>
            <Label htmlFor="email" className="text-[#0A1426] font-semibold">E-mail</Label>
            <Input id="email" type="email" data-testid="register-email-input" required value={form.email} onChange={onChange("email")} className="h-14 bg-white" />
          </div>
          <div>
            <Label htmlFor="phone" className="text-[#0A1426] font-semibold">Téléphone</Label>
            <Input id="phone" type="tel" data-testid="register-phone-input" value={form.telephone} onChange={onChange("telephone")} className="h-14 bg-white" />
          </div>
          <div>
            <Label htmlFor="password" className="text-[#0A1426] font-semibold">Mot de passe</Label>
            <Input id="password" type="password" data-testid="register-password-input" required minLength={6} value={form.password} onChange={onChange("password")} className="h-14 bg-white" />
          </div>
          {form.role === "therapeute" && (
            <div data-testid="register-centre-fields" className="rounded-2xl bg-[#F5F3EE] p-3 flex flex-col gap-2">
              <Label className="text-[#0A1426] font-semibold">Mon Centre Bombacare</Label>
              <Input data-testid="register-centre-nom" placeholder="Nom du centre (ex. Centre Bombacare Matadi)" value={form.centre_nom} onChange={onChange("centre_nom")} className="h-12 bg-white" />
              <Input data-testid="register-ville" placeholder="Ville" value={form.ville} onChange={onChange("ville")} className="h-12 bg-white" />
            </div>
          )}
          <div>
            <Label className="text-[#0A1426] font-semibold">Mon pays</Label>
            <div className="mt-1"><CountryGrid value={form.pays} onChange={(c) => setForm((f) => ({ ...f, pays: c }))} pro={form.role === "therapeute"} testPrefix="register-pays" /></div>
            <p className="text-[11px] text-[#0A1426]/60 mt-1">Le pays active les bonnes fonctionnalités : langues, devise, urgences, paiement et règles de facturation.</p>
          </div>
          <div>
            <Label className="text-[#0A1426] font-semibold">Je suis…</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="register-role-select" className="h-14 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="patient">Patient</SelectItem>
                <SelectItem value="aidant">Aidant familial</SelectItem>
                <SelectItem value="therapeute">Praticien</SelectItem>
                <SelectItem value="payeur">Payeur diaspora (je soutiens un proche)</SelectItem>
                <SelectItem value="etablissement">Établissement / clinique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#0A1426] font-semibold">Code promo ou parrainage (optionnel)</Label>
            <input data-testid="register-code-parrain" value={form.code_parrain} onChange={(e) => setForm((f) => ({ ...f, code_parrain: e.target.value.toUpperCase() }))} placeholder="BOMBA2026 ou BC-XXXXXX" className="mt-1 h-14 w-full px-4 rounded-xl border border-[#E4E0D5] bg-white text-sm text-[#0A1426]" />
          </div>
          <TarifsGrid pays={form.pays} roleFilter={form.role === "aidant" ? "patient" : form.role} compact testPrefix="register-tarif" />
          {error && <p data-testid="register-error" className="text-sm text-[#D9534F] font-medium">{error}</p>}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="h-14 text-base font-bold bg-[#2BA3A0] hover:bg-[#0A1426] text-white rounded-full"
          >
            {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            Créer mon compte
          </Button>
          <p className="text-center text-sm text-[#0A1426]/80">
            Déjà un compte ?{" "}
            <Link to="/auth/login" data-testid="goto-login" className="font-bold text-[#2BA3A0] underline">
              Se connecter
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
